import { SendOutlined } from '@ant-design/icons';
import { Button, Input, theme } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getTimeAgo } from '../../library/utilities/intl.js';

const isTeamMessage = (item) => item.source === 1

const CommunicationAnchor = ({ id, children, onVisible }) => {
    const location = useLocation()
    const sectionRef = useRef(null)
    const onVisibleRef = useRef(onVisible)
    onVisibleRef.current = onVisible

    // Fire once when the section scrolls into view (e.g. to mark messages seen).
    // No-op when onVisible is not provided — existing behaviour unchanged.
    useEffect(() => {
        if (typeof onVisibleRef.current !== 'function' || !sectionRef.current) {
            return
        }
        if (typeof IntersectionObserver === 'undefined') {
            onVisibleRef.current()
            return
        }
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                    observer.disconnect()
                    onVisibleRef.current?.()
                }
            },
            { threshold: 0.2 },
        )
        observer.observe(sectionRef.current)
        return () => observer.disconnect()
    }, [id])

    useEffect(() => {
        if (location.hash !== `#${id}` || !sectionRef.current) {
            return
        }

        let layoutFrame
        const scrollToSection = () => {
            cancelAnimationFrame(layoutFrame)
            layoutFrame = requestAnimationFrame(() => {
                sectionRef.current?.scrollIntoView({ behavior: 'auto', block: 'start' })
                sectionRef.current?.focus({ preventScroll: true })
            })
        }

        // The message history is deferred. Re-scroll after it replaces the
        // loading fallback, otherwise ScrollRestoration can leave the section
        // lower down the page.
        scrollToSection()
        const observer = typeof ResizeObserver === 'undefined'
            ? null
            : new ResizeObserver(scrollToSection)
        observer?.observe(sectionRef.current)

        return () => {
            cancelAnimationFrame(layoutFrame)
            observer?.disconnect()
        }
    }, [id, location.hash, location.pathname])

    return (
        <div id={id} ref={sectionRef} tabIndex={-1} style={{ scrollMarginTop: '14rem' }}>
            {children}
        </div>
    )
}

CommunicationAnchor.propTypes = {
    id: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    onVisible: PropTypes.func,
}

const ClientCommunicationAnchor = ({ children, onVisible }) => (
    <CommunicationAnchor id="client-communication" onVisible={onVisible}>{children}</CommunicationAnchor>
)

ClientCommunicationAnchor.propTypes = {
    children: PropTypes.node.isRequired,
    onVisible: PropTypes.func,
}

const CommunicationPanel = ({ messages, placeholder = "", onSubmit }) => {
    const { token } = theme.useToken()
    const [inputMessage, setInputMessage] = useState('')
    const listRef = useRef(null)

    const orderedMessages = useMemo(() => {
        const list = Array.isArray(messages) ? [...messages] : []
        return list.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
    }, [messages])

    useEffect(() => {
        if (listRef.current) {
            listRef.current.scrollTop = listRef.current.scrollHeight
        }
    }, [orderedMessages])

    const handleSend = () => {
        const comment = inputMessage.trim()
        if (comment?.length > 0) {
            onSubmit(comment)
            setInputMessage('')
        }
    }

    const handleKeyDown = (e) => {
        if (e.ctrlKey && e.key.toLowerCase() === 'j') {
            e.preventDefault()
            handleSend()
        }
    }

    return (
        <>
            <div
                ref={listRef}
                style={{
                    maxHeight: 300,
                    overflowY: 'auto',
                    padding: '12px',
                    marginBottom: '0.5rem',
                    background: token.colorBgContainer,
                    border: `1px solid ${token.colorBorder}`,
                    borderRadius: token.borderRadiusLG,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.8rem',
                }}
            >
                {orderedMessages.length === 0 && (
                    <div style={{ textAlign: 'center', color: token.colorTextSecondary, padding: '1.5rem 0' }}>
                        No messages yet
                    </div>
                )}
                {orderedMessages.map((item) => {
                    const isTeam = isTeamMessage(item)
                    const name = item.user?.name ?? item.sender_name ?? 'Leoprinting'
                    return (
                        <div key={item.id} style={{ display: 'flex', justifyContent: isTeam ? 'flex-end' : 'flex-start' }}>
                            <div style={{ maxWidth: '75%', display: 'flex', flexDirection: 'column', alignItems: isTeam ? 'flex-end' : 'flex-start' }}>
                                <div style={{ fontSize: 12, color: token.colorTextSecondary, marginBottom: 4, paddingInline: 4 }}>
                                    {name}
                                    <span style={{ marginLeft: 8, opacity: 0.8 }}>{getTimeAgo(item.createdAt)}</span>
                                </div>
                                <div
                                    style={{
                                        padding: '8px 12px',
                                        borderRadius: token.borderRadius,
                                        borderTopLeftRadius: isTeam ? token.borderRadius : 2,
                                        borderTopRightRadius: isTeam ? 2 : token.borderRadius,
                                        background: isTeam ? token.colorSuccess : token.colorFillTertiary,
                                        color: isTeam ? '#ffffff' : token.colorText,
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                                    }}
                                >
                                    {item.message}
                                </div>
                            </div>
                        </div>
                    )
                })}
            </div>
            <div className="chat-composer" style={{ display: 'flex', alignItems: 'flex-end', gap: '0.75rem' }}>
                <Input.TextArea
                    name="comment"
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    autoSize={{ minRows: 1, maxRows: 6 }}
                    style={{ flex: 1 }}
                />
                <Button
                    type="primary"
                    icon={<SendOutlined />}
                    iconPosition="end"
                    disabled={inputMessage.trim().length === 0}
                    onClick={handleSend}
                >Send</Button>
            </div>
            <div style={{ fontSize: 12, color: token.colorTextSecondary, marginTop: '0.25rem' }}>
                Press Ctrl+J to send
            </div>
        </>
    )
}
CommunicationPanel.propTypes = {
    messages: PropTypes.arrayOf(PropTypes.shape({
        message: PropTypes.string.isRequired,
        user: PropTypes.object,
        sender_name: PropTypes.string,
        source: PropTypes.number.isRequired,
    })),
    onSubmit: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
}

export { ClientCommunicationAnchor, CommunicationAnchor, CommunicationPanel };
